import { createI18n } from 'vue-i18n';
import type { I18nOptions } from 'vue-i18n';

import de from './de.json';

const messages = {
    de,
};

const numberFormats = {
    de: {
        currency: {
            style: 'currency',
            currency: 'EUR',
        },
        decimal: {
            style: 'decimal',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        },
    },
    en: {
        currency: {
            style: 'currency',
            currency: 'USD',
        },
        decimal: {
            style: 'decimal',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        },
    },
} as const satisfies I18nOptions['numberFormats'];

/** Timezone the Frontend uses to display Dates. */
export const defaultTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
/** Timezone the Backend sends Date in. */
export const backendTimeZone = 'GMT';

const datetimeFormatOptions = {
    de: {
        short: {
            timeZone: defaultTimeZone,
            year: '2-digit',
            month: '2-digit',
            day: '2-digit',
        },
        longDate: {
            timeZone: defaultTimeZone,
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        },
        shortWithMonth: {
            timeZone: defaultTimeZone,
            year: '2-digit',
            month: 'long',
            day: 'numeric',
        },
        shortWithWeekday: {
            timeZone: defaultTimeZone,
            year: '2-digit',
            month: 'long',
            day: '2-digit',
            weekday: 'long',
        },
        long: {
            timeZone: defaultTimeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        },
        time: {
            timeZone: defaultTimeZone,
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        },
        calendarMonth: {
            month: 'long',
            year: 'numeric',
        },
        calendarYear: {
            year: 'numeric',
        },
    },
    en: {
        short: {
            timeZone: defaultTimeZone,
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        },
        shortWithMonth: {
            timeZone: defaultTimeZone,
            year: '2-digit',
            month: 'long',
            day: 'numeric',
        },
        shortWithWeekday: {
            timeZone: defaultTimeZone,
            year: '2-digit',
            month: 'long',
            day: '2-digit',
            weekday: 'long',
        },
        long: {
            timeZone: defaultTimeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        },
        time: {
            timeZone: defaultTimeZone,
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        },
        calendarMonth: {
            month: 'long',
            year: 'numeric',
        },
        calendarYear: {
            year: 'numeric',
        },
    },
} as const satisfies I18nOptions['datetimeFormats'];

export type MessageSchema = typeof de;

export const i18n = createI18n<[MessageSchema], 'de'>({
    messages,
    numberFormats,
    legacy: false,
    locale: 'de',
    fallbackLocale: 'de',
    datetimeFormats: datetimeFormatOptions,
});
